<h2 class="page-title">Supplier Metadata</h2>

<div class="card-section">
  <h5 class="mb-3">General</h5>
  <div class="row g-3">

    <div class="col-12 col-md-6">
      <label class="form-label">Supplier name</label>
      <input type="text" class="form-control"
        [(ngModel)]="this.data.supplier_implementation.basic_supplier_info.name" disabled>
    </div>

    <div class="col-12 col-md-6">
      <label class="form-label">Supplier ID</label>
      <input type="text" class="form-control"
        [(ngModel)]="this.data.supplier_implementation.basic_supplier_info.supplier_id" disabled>
    </div>

    <div class="col-12">
      <label class="form-label">Base URL</label>
      <input type="text" class="form-control"
        [(ngModel)]="this.data.supplier_implementation.basic_supplier_info.base_url">
    </div>

    <div class="col-12">
      <label class="form-label">
        Search URL
        <span class="text-muted small ms-1">(replace search term with &#123;number&#125;)</span>
      </label>
      <input type="text" class="form-control"
        [(ngModel)]="this.data.supplier_implementation.basic_supplier_info.search_url">
    </div>

    <div class="col-12 col-md-6">
      <label class="form-label">
        Country
        <span class="text-muted small ms-1"
          matTooltip="When in doubt about the location of the shop, use the imprint/impressum">(?)</span>
      </label>
      <select class="form-select"
        [(ngModel)]="this.data.supplier_implementation.basic_supplier_info.country" required>
        <option value="">Select a country</option>
        @for (code of countries; track code) {
          <option [value]="code">{{ code }}</option>
        }
      </select>
    </div>

    <div class="col-12 col-md-6">
      <label class="form-label">
        Language
        <span class="text-muted small ms-1"
          matTooltip="The language the website uses when navigating with the base URL without changing settings">(?)</span>
      </label>
      <select class="form-select"
        [(ngModel)]="this.data.supplier_implementation.basic_supplier_info.language" required>
        <option value="">Select a language</option>
        @for (code of languages; track code) {
          <option [value]="code">{{ code }}</option>
        }
      </select>
    </div>

    <div class="col-12 col-md-6">
      <label class="form-label">
        Decimal separator
        <span class="text-muted small ms-1"
          matTooltip="The character used to separate the integer part from the fractional part of a number">(?)</span>
      </label>
      <select class="form-select"
        [(ngModel)]="this.data.supplier_implementation.basic_supplier_info.decimal_separator" required>
        <option value="">Select a decimal separator</option>
        @for (separator of decimal_separator; track separator) {
          <option [value]="separator">{{ separator }}</option>
        }
      </select>
    </div>

  </div>
</div>

<div class="card-section">
  <h5 class="mb-3">Currency</h5>
  <div class="row g-3">

    <div class="col-12 col-md-6">
      <label class="form-label">
        Default currency
        <span class="text-muted small ms-1"
          matTooltip="This is the primary currency of the shop, without changing language or other settings">(?)</span>
      </label>
      <select class="form-select"
        [(ngModel)]="this.data.supplier_implementation.basic_supplier_info.currency" required>
        <option value="">Select a currency</option>
        @for (code of currencies; track code) {
          <option [value]="code">{{ code }}</option>
        }
      </select>
    </div>

    <div class="col-12">
      <label class="form-label">
        Currency mapping
        <span class="text-muted small ms-1"
          matTooltip="Maps the currency text shown on this supplier's pages (e.g. 'C $') to an ISO code. Only needed for ambiguous texts like '$' or 'kr' when the shop shows more than one currency; unambiguous symbols (€, £, US$, ...) and ISO codes are detected automatically. Anything unresolved falls back to the currency above.">(?)</span>
      </label>
      <table class="table table-sm align-middle mb-2">
        <thead>
          <tr>
            <th>Text on page</th>
            <th>Currency</th>
            <th style="width:3rem"></th>
          </tr>
        </thead>
        <tbody>
          @for (row of currencyMappingRows; track $index) {
            <tr>
              <td>
                <input type="text" class="form-control form-control-sm" placeholder="e.g. C $"
                  [(ngModel)]="row.currency_text" (ngModelChange)="syncCurrencyMapping()">
              </td>
              <td>
                <select class="form-select form-select-sm" [(ngModel)]="row.currency" (ngModelChange)="syncCurrencyMapping()">
                  <option value="">Select a currency</option>
                  @for (code of currencies; track code) {
                    <option [value]="code">{{ code }}</option>
                  }
                </select>
              </td>
              <td>
                <button type="button" class="btn btn-sm btn-outline-danger" (click)="removeCurrencyMappingRow($index)">&times;</button>
              </td>
            </tr>
          } @empty {
            <tr><td colspan="3" class="text-muted small">No mapping. Currency is detected automatically or defaults to {{ this.data.supplier_implementation.basic_supplier_info.currency || 'the supplier currency' }}.</td></tr>
          }
        </tbody>
      </table>
      @if (currencyMappingError) {
        <div class="text-danger small mb-2">{{ currencyMappingError }}</div>
      }
      <div class="d-flex flex-wrap gap-2 align-items-center">
        <button type="button" class="btn btn-sm btn-secondary" (click)="addCurrencyMappingRow()">Add mapping</button>
        <input type="text" class="form-control form-control-sm w-auto" placeholder="Test text, e.g. C $15.00"
          [(ngModel)]="currencyPreviewText" (keyup.enter)="previewCurrency()">
        <button type="button" class="btn btn-sm btn-primary" (click)="previewCurrency()">Test</button>
        @if (currencyPreviewResult) {
          <span class="small">Result: <strong>{{ currencyPreviewResult }}</strong></span>
        }
      </div>
    </div>

  </div>
</div>

<div class="card-section">
  <h5 class="mb-3">VAT</h5>
  <div class="row g-3">

    <div class="col-12 d-flex align-items-center gap-2">
      <input type="checkbox" class="form-check-input mt-0" id="vatCheck"
        [(ngModel)]="this.data.supplier_implementation.basic_supplier_info.vat_included_on_displayed_price">
      <label class="form-check-label" for="vatCheck">
        VAT included in the displayed price? (default)
        <span class="text-muted small ms-1"
          matTooltip="Supplier default, used for pages whose VAT status can't be read off the page (no VAT text, no schema valueAddedTaxIncluded). Pages that say e.g. 'inkl. MwSt.' or 'excl. VAT' override it.">(?)</span>
      </label>
    </div>

    <div class="col-12">
      <label class="form-label">
        Price status mapping
        <span class="text-muted small ms-1"
          matTooltip="Maps a text shown on this supplier's pages instead of (or next to) a price (e.g. 'Get a price', 'Preis nach Login') to the status it means. Checked before the built-in keywords wherever a price, price status or condition text is classified. Only needed for wordings the built-in keywords don't know.">(?)</span>
      </label>
      <table class="table table-sm align-middle mb-2">
        <thead>
          <tr>
            <th>Text on page</th>
            <th>Status</th>
            <th style="width:3rem"></th>
          </tr>
        </thead>
        <tbody>
          @for (row of priceStatusMappingRows; track $index) {
            <tr>
              <td>
                <input type="text" class="form-control form-control-sm" placeholder="e.g. Get a price"
                  [(ngModel)]="row.text" (ngModelChange)="syncPriceStatusMapping()">
              </td>
              <td>
                <select class="form-select form-select-sm" [(ngModel)]="row.status" (ngModelChange)="syncPriceStatusMapping()">
                  <option [ngValue]="null">Select</option>
                  @for (option of priceStatusOptions; track option.value) {
                    <option [ngValue]="option.value">{{ option.label }}</option>
                  }
                </select>
              </td>
              <td>
                <button type="button" class="btn btn-sm btn-outline-danger" (click)="removePriceStatusMappingRow($index)">&times;</button>
              </td>
            </tr>
          } @empty {
            <tr><td colspan="3" class="text-muted small">No mapping. Only the built-in keywords are used.</td></tr>
          }
        </tbody>
      </table>
      @if (priceStatusMappingError) {
        <div class="text-danger small mb-2">{{ priceStatusMappingError }}</div>
      }
      <div class="d-flex flex-wrap gap-2 align-items-center mb-3">
        <button type="button" class="btn btn-sm btn-secondary" (click)="addPriceStatusMappingRow()">Add mapping</button>
        <input type="text" class="form-control form-control-sm w-auto" placeholder="Test text, e.g. Get a price"
          [(ngModel)]="priceStatusPreviewText" (keyup.enter)="previewPriceStatus()">
        <button type="button" class="btn btn-sm btn-primary" (click)="previewPriceStatus()">Test</button>
        @if (priceStatusPreviewResult) {
          <span class="small">Result: <strong>{{ priceStatusPreviewResult }}</strong></span>
        }
      </div>
    </div>

    <div class="col-12">
      <label class="form-label">
        VAT mapping
        <span class="text-muted small ms-1"
          matTooltip="Maps the VAT text shown on this supplier's pages (e.g. 'Bruttopreis', 'prix public') to whether the displayed price includes VAT. Only needed for wordings the built-in markers don't know; common ones ('inkl. MwSt.', 'zzgl. MwSt.', 'excl. VAT', 'TTC', 'HT', ...) are detected automatically. Pages that say nothing use the default above.">(?)</span>
      </label>
      <table class="table table-sm align-middle mb-2">
        <thead>
          <tr>
            <th>Text on page</th>
            <th>VAT</th>
            <th style="width:3rem"></th>
          </tr>
        </thead>
        <tbody>
          @for (row of vatMappingRows; track $index) {
            <tr>
              <td>
                <input type="text" class="form-control form-control-sm" placeholder="e.g. Bruttopreis"
                  [(ngModel)]="row.vat_text" (ngModelChange)="syncVatMapping()">
              </td>
              <td>
                <select class="form-select form-select-sm" [(ngModel)]="row.vat_included" (ngModelChange)="syncVatMapping()">
                  <option [ngValue]="null">Select</option>
                  <option [ngValue]="true">VAT included</option>
                  <option [ngValue]="false">VAT excluded</option>
                </select>
              </td>
              <td>
                <button type="button" class="btn btn-sm btn-outline-danger" (click)="removeVatMappingRow($index)">&times;</button>
              </td>
            </tr>
          } @empty {
            <tr><td colspan="3" class="text-muted small">No mapping. VAT status is detected automatically or defaults to {{ this.data.supplier_implementation.basic_supplier_info.vat_included_on_displayed_price ? 'VAT included' : 'VAT excluded' }}.</td></tr>
          }
        </tbody>
      </table>
      @if (vatMappingError) {
        <div class="text-danger small mb-2">{{ vatMappingError }}</div>
      }
      <div class="d-flex flex-wrap gap-2 align-items-center">
        <button type="button" class="btn btn-sm btn-secondary" (click)="addVatMappingRow()">Add mapping</button>
        <input type="text" class="form-control form-control-sm w-auto" placeholder="Test text, e.g. 19,99 € inkl. MwSt."
          [(ngModel)]="vatPreviewText" (keyup.enter)="previewVat()">
        <button type="button" class="btn btn-sm btn-primary" (click)="previewVat()">Test</button>
        @if (vatPreviewResult) {
          <span class="small">Result: <strong>{{ vatPreviewResult }}</strong></span>
        }
      </div>
    </div>

  </div>
</div>
